import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress } from '@mui/material';
import { FiX } from 'react-icons/fi';
import { createTutorial, updateTutorial } from '../../../../../app/store/slices/adminSlice';
import { toast } from 'react-toastify';

const TutorialFormModal = ({ open, onClose, tutorial, onSuccess }) => {
  const dispatch = useDispatch();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: '',
    description: '',
    link: '',
    sequence: 1,
    isActive: true,
    thumbnailUrl: '',
    duration: '',
    tags: [],
    level: 'beginner',
    category: ''
  });

  const [tagInput, setTagInput] = useState('');
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (tutorial) {
      setForm({
        title: tutorial.title || '',
        description: tutorial.description || '',
        link: tutorial.link || '',
        sequence: tutorial.sequence || 1,
        isActive: tutorial.isActive ?? true,
        thumbnailUrl: tutorial.thumbnailUrl || '',
        duration: tutorial.duration || '',
        tags: tutorial.tags || [],
        level: tutorial.level || 'beginner',
        category: tutorial.category || ''
      });
    } else {
      setForm({
        title: '',
        description: '',
        link: '',
        sequence: 1,
        isActive: true,
        thumbnailUrl: '',
        duration: '',
        tags: [],
        level: 'beginner',
        category: ''
      });
    }
    setErrors({});
  }, [tutorial, open]);

  const validateForm = () => {
    const newErrors = {};

    if (!form.title.trim()) {
      newErrors.title = 'Title is required';
    } else if (form.title.length > 200) {
      newErrors.title = 'Title must be 200 characters or less';
    }

    if (!form.description.trim()) {
      newErrors.description = 'Description is required';
    } else if (form.description.length > 1000) {
      newErrors.description = 'Description must be 1000 characters or less';
    }

    if (!form.link.trim()) {
      newErrors.link = 'Link is required';
    } else if (!isValidUrl(form.link)) {
      newErrors.link = 'Please enter a valid URL';
    }

    if (!form.sequence || form.sequence < 1) {
      newErrors.sequence = 'Sequence must be at least 1';
    }

    if (form.thumbnailUrl && !isValidUrl(form.thumbnailUrl)) {
      newErrors.thumbnailUrl = 'Please enter a valid URL';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const handleChange = (field, value) => {
    setForm(f => ({ ...f, [field]: value }));
    if (errors[field]) {
      setErrors(e => ({ ...e, [field]: undefined }));
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !form.tags.includes(tagInput.trim())) {
      setForm(f => ({ ...f, tags: [...f.tags, tagInput.trim()] }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    setForm(f => ({ ...f, tags: f.tags.filter(tag => tag !== tagToRemove) }));
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the errors in the form');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        sequence: Number(form.sequence)
      };
      if (!payload.thumbnailUrl) {
        delete payload.thumbnailUrl;
      }

      if (tutorial) {
        await dispatch(updateTutorial({ id: tutorial._id, data: payload })).unwrap();
        toast.success('Tutorial updated successfully');
      } else {
        await dispatch(createTutorial(payload)).unwrap();
        toast.success('Tutorial created successfully');
      }
      onSuccess();
    } catch (error) {
      toast.error(`Failed to save tutorial: ${error}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle className="flex justify-between items-center border-b pb-4">
        <span className="text-xl font-bold text-btn-primary">
          {tutorial ? 'Edit Tutorial' : 'Create Tutorial'}
        </span>
        <button
          onClick={onClose}
          disabled={saving}
          className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
        >
          <FiX size={24} />
        </button>
      </DialogTitle>

      <DialogContent className="mt-4">
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => handleChange('title', e.target.value)}
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-btn-primary ${
                errors.title ? 'border-red-500' : 'border-gray-300'
              }`}
              maxLength={200}
            />
            {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title}</p>}
            <p className="text-xs text-gray-500 mt-1">{form.title.length}/200 characters</p>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description <span className="text-red-500">*</span>
            </label>
            <textarea
              value={form.description}
              onChange={(e) => handleChange('description', e.target.value)}
              rows={4}
              className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-btn-primary ${
                errors.description ? 'border-red-500' : 'border-gray-300'
              }`}
              maxLength={1000}
            />
            {errors.description && <p className="text-red-500 text-xs mt-1">{errors.description}</p>}
            <p className="text-xs text-gray-500 mt-1">{form.description.length}/1000 characters</p>
          </div>

          {/* Link and Thumbnail URL */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Video Link <span className="text-red-500">*</span>
              </label>
              <input
                type="url"
                value={form.link}
                onChange={(e) => handleChange('link', e.target.value)}
                placeholder="https://youtube.com/..."
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-btn-primary ${
                  errors.link ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.link && <p className="text-red-500 text-xs mt-1">{errors.link}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Thumbnail URL
              </label>
              <input
                type="url"
                value={form.thumbnailUrl}
                onChange={(e) => handleChange('thumbnailUrl', e.target.value)}
                placeholder="https://..."
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-btn-primary ${
                  errors.thumbnailUrl ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.thumbnailUrl && <p className="text-red-500 text-xs mt-1">{errors.thumbnailUrl}</p>}
            </div>
          </div>

          {/* Sequence, Duration, Category */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sequence <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                value={form.sequence}
                onChange={(e) => handleChange('sequence', e.target.value)}
                min="1"
                className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-btn-primary ${
                  errors.sequence ? 'border-red-500' : 'border-gray-300'
                }`}
              />
              {errors.sequence && <p className="text-red-500 text-xs mt-1">{errors.sequence}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Duration
              </label>
              <input
                type="text"
                value={form.duration}
                onChange={(e) => handleChange('duration', e.target.value)}
                placeholder="e.g., 5:30"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-btn-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <input
                type="text"
                value={form.category}
                onChange={(e) => handleChange('category', e.target.value)}
                placeholder="e.g., Budget"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-btn-primary"
              />
            </div>
          </div>

          {/* Level and Active Status */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Level
              </label>
              <select
                value={form.level}
                onChange={(e) => handleChange('level', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-btn-primary"
              >
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <div className="flex items-center h-full">
                <label className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(e) => handleChange('isActive', e.target.checked)}
                    className="w-4 h-4 text-btn-primary border-gray-300 rounded focus:ring-btn-primary"
                  />
                  <span className="ml-2 text-sm text-gray-700">Active</span>
                </label>
              </div>
            </div>
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Tags
            </label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Add a tag and press Enter"
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-btn-primary"
              />
              <button
                type="button"
                onClick={handleAddTag}
                className="px-4 py-2 bg-btn-secondary text-btn-secondary-text rounded-lg hover:bg-btn-secondary-hover transition-colors"
              >
                Add
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {form.tags.map((tag, index) => (
                <span
                  key={index}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                >
                  {tag}
                  <button
                    type="button"
                    onClick={() => handleRemoveTag(tag)}
                    className="text-gray-500 hover:text-red-600"
                  >
                    <FiX size={14} />
                  </button>
                </span>
              ))}
            </div>
          </div>
        </form>
      </DialogContent>

      <DialogActions className="border-t px-6 py-4">
        <button
          onClick={onClose}
          disabled={saving}
          className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={saving}
          className="px-4 py-2 bg-btn-primary text-btn-primary-text rounded-lg hover:bg-btn-primary-hover disabled:opacity-50 flex items-center gap-2"
        >
          {saving && <CircularProgress size={16} color="inherit" />}
          {tutorial ? 'Update' : 'Create'}
        </button>
      </DialogActions>
    </Dialog>
  );
};

export default TutorialFormModal;